import React, { useEffect, useMemo, useRef, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Popover,
  PopoverBody,
  UncontrolledDropdown,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faEllipsisV, faListCheck, faRocket } from "@fortawesome/free-solid-svg-icons";

import DateParse from "../../../components/DateParse";
import ErrorScreen from "../../../components/error/ErrorScreen";
import Loader from "../../../components/Loader";
import { alertContext } from "../../../components/modal/ModalComponent";
import useFetch from "../../../components/useFetch";
import {
  AccuracyIcon2,
  CalendarIcon,
  GraphIcon,
  LabelsIcon,
  ModelIcon,
  ModifyOperationsIcon,
} from "../../Common/SvgIcons/WorkflowIcons";
import ClassLabelsPreview from "../train/ClassLabelsPreview";
import nivoTheme from "../train/graph/nivoTheme";
import "../data/Dataset.scss";
import AddExternalModel from "../../hub/AddExternalModel";
import ModelArchitectureSidebar from "./ModelArchitectureSidebar";
import { getArchMeta, sortArchitectureKeys } from "./Models";
import "../train/Experiments.scss";
import "./Models.scss";

const titleCase = (value, fallback = "-") => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const formatVersion = (value) => {
  if (value === undefined || value === null || value === "") return null;
  return `V${String(value).replace(/^v/i, "")}`;
};

const shortValue = (value, maxLength = 10) => {
  const text = String(value ?? "-");
  return text.length > maxLength ? `${text.slice(0, maxLength).trim()}…` : text;
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

function ModelVersionActionInsight({
  version,
  activeIndex = 0,
  onActiveIndexChange,
  onEvaluate,
  onDeploy,
}) {
  const [phase, setPhase] = useState("enter");
  const [paused, setPaused] = useState(false);
  const activeIndexRef = useRef(activeIndex);

  const actions = useMemo(() => {
    const versionLabel = formatVersion(version) || "this version";

    return [
      {
        key: "evaluation",
        tone: "task",
        eyebrow: "Evaluation next",
        title: "Evaluate Model",
        description: `Run benchmark and performance checks for ${versionLabel}`,
        actionLabel: "Evaluate",
        icon: <FontAwesomeIcon icon={faBolt} className="dataset-version-insight__icon" />,
        onClick: onEvaluate,
      },
      {
        key: "deploy",
        tone: "experiment",
        eyebrow: "Deployment next",
        title: "Deploy",
        description: `Send ${versionLabel} to supported deployment targets`,
        actionLabel: "Deploy",
        icon: <FontAwesomeIcon icon={faRocket} className="dataset-version-insight__icon" />,
        onClick: onDeploy,
      },
    ];
  }, [onDeploy, onEvaluate, version]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
    setPhase("enter");
  }, [activeIndex, version]);

  useEffect(() => {
    if (paused || phase !== "enter") return undefined;

    const holdTimer = window.setTimeout(() => setPhase("exit"), 4200);
    return () => window.clearTimeout(holdTimer);
  }, [activeIndex, paused, phase, version]);

  useEffect(() => {
    if (phase !== "exit") return undefined;

    const swapTimer = window.setTimeout(() => {
      const nextIndex = (activeIndexRef.current + 1) % actions.length;
      activeIndexRef.current = nextIndex;
      onActiveIndexChange?.(nextIndex);
      setPhase("enter");
    }, 380);

    return () => window.clearTimeout(swapTimer);
  }, [actions.length, onActiveIndexChange, phase]);

  const active = actions[activeIndex] || actions[0];

  return (
    <div
      id="deploy-version-model-button"
      className="dataset-version-insight dataset-buttons-outer"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div
        key={`${active.key}-${activeIndex}`}
        className={`dataset-version-insight__panel dataset-version-insight__panel--${active.tone} is-${phase}`}
      >
        <div className="dataset-version-insight__icon-wrap" aria-hidden="true">
          {active.icon}
        </div>
        <div className="dataset-version-insight__copy">
          <span className="dataset-version-insight__eyebrow">{active.eyebrow}</span>
          <strong className="dataset-version-insight__title">{active.title}</strong>
          <p className="dataset-version-insight__description">{active.description}</p>
        </div>
        <button
          type="button"
          className="dataset-version-insight__cta"
          onClick={active.onClick}
        >
          {active.actionLabel}
        </button>
      </div>
    </div>
  );
}

function ExpandableModelCards({ cards }) {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="training-metric-strip training-metric-strip--interactive deploy-model-detail__cards">
      {cards.map((card, index) => {
        const expanded = activeIndex === index;
        const activate = () => setActiveIndex(index);

        return (
          <article
            key={card.id}
            className={`training-metric-panel tone-${card.tone} ${expanded ? "is-expanded" : "is-collapsed"}`}
            onMouseEnter={activate}
            onFocus={activate}
            onClick={activate}
            tabIndex={0}
          >
            <div className="training-metric-panel-inner">
              <div className="training-metric-panel-collapsed" aria-hidden={expanded}>
                <span className="training-metric-panel-short-label">{card.shortLabel}</span>
                <span className="training-metric-panel-short-value">{card.shortValue}</span>
                <span className="training-metric-panel-icon">{card.icon}</span>
              </div>

              <div className="training-metric-panel-expanded" aria-hidden={!expanded}>
                <div className="training-metric-panel-expanded-head">
                  <span className="training-metric-panel-label">{card.label}</span>
                  <span className="training-metric-panel-icon-large">{card.icon}</span>
                </div>
                <h3 className="training-metric-panel-value">{card.title}</h3>
                <p className="training-metric-panel-description">{card.description}</p>
                <dl className="training-metric-panel-details">
                  {card.details.map((detail) => (
                    <div key={detail.label} className="training-metric-panel-detail-row">
                      <dt>{detail.label}</dt>
                      <dd>{detail.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function ModelMetrics({ rows, metricName }) {
  const latencyData = rows
    .map((row, index) => ({
      target: row.label || `Target ${index + 1}`,
      latency: toNumber(row.latency),
    }))
    .filter((row) => row.latency !== null);
  const accuracyData = rows
    .map((row, index) => ({
      x: row.label || `Target ${index + 1}`,
      y: toNumber(row.accuracy),
    }))
    .filter((row) => row.y !== null);

  if (!latencyData.length && !accuracyData.length) {
    return (
      <div className="deploy-model-detail__empty container-card">
        <GraphIcon className="wf-icon text-secondary" />
        <h5>No benchmark metrics available</h5>
        <p className="text-secondary mb-0">Charts will appear when this model version has benchmark results.</p>
      </div>
    );
  }

  return (
    <div className="deploy-model-detail__chart-grid">
      {accuracyData.length > 0 && (
        <section className="training-metric-card">
          <div className="training-metric-card__header">
            <div>
              <h4>{titleCase(metricName, "Accuracy")}</h4>
              <p className="text-secondary mb-0">Metric achieved across target hardware.</p>
            </div>
          </div>
          <div className="training-metric-card__body">
            <ResponsiveLine
              data={[{ id: metricName || "Accuracy", data: accuracyData }]}
              margin={{ top: 28, right: 28, bottom: 62, left: 58 }}
              xScale={{ type: "point" }}
              yScale={{ type: "linear", min: "auto", max: "auto", stacked: false }}
              curve="monotoneX"
              colors={["#10b981"]}
              pointSize={8}
              pointColor="#10b981"
              pointBorderWidth={2}
              pointBorderColor={{ from: "serieColor" }}
              enableArea
              areaOpacity={0.08}
              useMesh
              axisBottom={{ tickRotation: -20, legend: "Target hardware", legendOffset: 52, legendPosition: "middle" }}
              axisLeft={{ legend: titleCase(metricName, "Metric"), legendOffset: -46, legendPosition: "middle" }}
              theme={nivoTheme}
            />
          </div>
        </section>
      )}

      {latencyData.length > 0 && (
        <section className="training-metric-card">
          <div className="training-metric-card__header">
            <div>
              <h4>Inference Performance</h4>
              <p className="text-secondary mb-0">Lower latency means faster inference.</p>
            </div>
          </div>
          <div className="training-metric-card__body">
            <ResponsiveBar
              data={latencyData}
              keys={["latency"]}
              indexBy="target"
              margin={{ top: 28, right: 28, bottom: 62, left: 58 }}
              padding={0.38}
              colors={["#3755d3"]}
              borderRadius={3}
              axisBottom={{ tickRotation: -20, legend: "Target hardware", legendOffset: 52, legendPosition: "middle" }}
              axisLeft={{ legend: "Latency (ms)", legendOffset: -46, legendPosition: "middle" }}
              enableLabel={false}
              theme={nivoTheme}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function ModelConfiguration({ info, classNames }) {
  const modelInfo = info?.config?.Model_Info || {};
  const preprocessing = modelInfo?.Preprocessing || {};
  const entries = [
    ["Framework", titleCase(info?.framework)],
    ["Graph format", Array.isArray(info?.model_graph_format) ? info.model_graph_format.join(", ") : info?.model_graph_format || modelInfo?.["Model Input Format"] || "-"],
    ["Dataset", titleCase(info?.benchmark_dataset)],
    ["Model variant", titleCase(info?.model_variant)],
    ["Input resolution", firstValue(preprocessing?.["max input resolution"], info?.input_resolution, "-")],
    ["Interpolation", titleCase(preprocessing?.interpolation)],
    ["Normalization", titleCase(preprocessing?.normalization_method)],
    ["Grayscale", preprocessing?.GrayScale ? "Enabled" : "Disabled"],
    ["Classes", classNames.length || "-"],
    ["Ownership", titleCase(info?.ownership)],
  ];

  return (
    <section className="container-card deploy-model-detail__configuration">
      <div className="deploy-model-detail__configuration-head">
        <div>
          <h4>Model Configuration</h4>
          <p className="text-secondary mb-0">Version-specific model metadata and preprocessing settings.</p>
        </div>
      </div>
      <dl>
        {entries.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export default function ModelVersionDetail() {
  const { projectId, architecture, modelName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { onPathDetailChange } = useOutletContext() || {};
  const [searchParams] = useSearchParams();
  const [activePanel, setActivePanel] = useState("metrics");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModelSearch, setShowModelSearch] = useState(false);
  const [isAddModelOpen, setIsAddModelOpen] = useState(false);
  const [addModelAlert, setAddModelAlert] = useState();
  const [showDeployPopover, setShowDeployPopover] = useState(false);
  const [deployPopoverTarget, setDeployPopoverTarget] = useState("deploy-version-model-button");
  const [actionInsightIndex, setActionInsightIndex] = useState(0);
  const deployPopoverTimeoutRef = useRef(null);
  const selectedCard = location.state?.selectedCard;
  const requestedVersion = firstValue(location.state?.version, selectedCard?.version, searchParams.get("version"));
  const requestedModelId = firstValue(selectedCard?._id, searchParams.get("modelId"));
  const decodedModelName = decodeURIComponent(modelName || selectedCard?.name || "");

  const filters = useMemo(() => {
    if (requestedModelId) return { _id: requestedModelId };
    const value = { name: decodedModelName };
    if (requestedVersion !== undefined && requestedVersion !== null && requestedVersion !== "") {
      value.version = requestedVersion;
    }
    return value;
  }, [decodedModelName, requestedModelId, requestedVersion]);

  const url = `models?filters=${JSON.stringify(filters)}&section=evaluation`;
  const { loading, data, error, refetch } = useFetch(url);
  const modelsUrl = `models?filters={"project_id":"${projectId}"}&visual_inspection=false`;
  const { data: architectureData } = useFetch(modelsUrl);
  const info = data?.info;
  const version = firstValue(info?.version, requestedVersion);
  const displayName = String(firstValue(info?.name, decodedModelName, "Model"));
  const architectureCards = useMemo(() => {
    if (!architectureData || Array.isArray(architectureData)) return [];
    return sortArchitectureKeys(Object.keys(architectureData)).map((key) =>
      getArchMeta(key, architectureData[key])
    );
  }, [architectureData]);
  const listedModel = useMemo(() => {
    if (!architectureData || Array.isArray(architectureData)) return null;

    const architectureRows = architectureData?.[architecture]?.data || [];
    const allRows = Object.values(architectureData).flatMap((bucket) =>
      Array.isArray(bucket?.data) ? bucket.data : []
    );
    const candidates = architectureRows.length ? architectureRows : allRows;
    const normalizedVersion = String(requestedVersion ?? "").replace(/^v/i, "");

    return (
      candidates.find((model) => String(model?._id) === String(requestedModelId)) ||
      candidates.find(
        (model) =>
          String(model?.name || "").toLowerCase() === decodedModelName.toLowerCase() &&
          String(model?.version ?? "").replace(/^v/i, "") === normalizedVersion
      ) ||
      candidates[0] ||
      null
    );
  }, [architecture, architectureData, decodedModelName, requestedModelId, requestedVersion]);
  const modelConfig = firstValue(info?.config, selectedCard?.config, listedModel?.config, {});

  useEffect(() => {
    if (!onPathDetailChange) return undefined;
    onPathDetailChange({
      extras: [{ label: `${displayName}${formatVersion(version) ? ` - ${formatVersion(version)}` : ""}` }],
      onBack: () => navigate(`/projects/${projectId}/deepdeploy/${encodeURIComponent(architecture)}`),
    });
    return () => onPathDetailChange(null);
  }, [architecture, displayName, navigate, onPathDetailChange, projectId, version]);

  useEffect(() => () => {
    if (deployPopoverTimeoutRef.current) {
      clearTimeout(deployPopoverTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    setActionInsightIndex(0);
  }, [requestedModelId, requestedVersion]);

  const classNames = useMemo(() => {
    const configuredClasses = firstValue(
      info?.config?.exp_config?.data?.class_names,
      selectedCard?.config?.exp_config?.data?.class_names,
      listedModel?.config?.exp_config?.data?.class_names
    );
    if (Array.isArray(configuredClasses)) return configuredClasses;
    const objectsOfInterest = firstValue(
      info?.config?.Model_Info?.["Objects of Interest"],
      selectedCard?.config?.Model_Info?.["Objects of Interest"],
      listedModel?.config?.Model_Info?.["Objects of Interest"],
      selectedCard?.objects_of_interest,
      listedModel?.objects_of_interest,
      ""
    );
    return String(objectsOfInterest)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }, [info, listedModel, selectedCard]);

  const benchmarkInfo = useMemo(
    () => info?.config?.Benchmark_Info || {},
    [info]
  );
  const benchmarkRows = useMemo(() => {
    const rows = benchmarkInfo?.Benchmarkings || info?.metrics || [];
    return (Array.isArray(rows) ? rows : []).map((row, index) => ({
      label: titleCase(firstValue(row?.["device type"], row?.device_type, row?.["device name"], row?.device_name), `Target ${index + 1}`),
      latency: firstValue(row?.["Performance(ms)"], row?.performance, row?.latency),
      accuracy: firstValue(row?.["Metric Value(%)"], row?.metric_value, row?.value),
    }));
  }, [benchmarkInfo, info?.metrics]);

  const closeModelSearch = () => {
    setShowModelSearch(false);
    setSearchTerm("");
  };

  const closeAddModelDrawer = () => {
    setAddModelAlert();
    setIsAddModelOpen(false);
  };

  const detailShell = (content) => (
    <>
      <div className="dataset-container gap-3 gap-md-4 deploy-landing deploy-detail-layout">
        <ModelArchitectureSidebar
          cards={architectureCards}
          selectedArchitecture={architecture}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          showSearch={showModelSearch}
          onShowSearch={() => setShowModelSearch(true)}
          onCloseSearch={closeModelSearch}
          onImport={() => {
            setAddModelAlert();
            setIsAddModelOpen(true);
          }}
          onSelect={(item) => {
            sessionStorage.setItem(`lastSelectedModel_${projectId}`, item.key);
            navigate(`/projects/${projectId}/deepdeploy/${encodeURIComponent(item.key)}`);
          }}
        />
        <div className="Dataset-version-container d-flex flex-column gap-3 gap-md-4">
          {content}
        </div>
      </div>

      {isAddModelOpen && (
        <alertContext.Provider value={setAddModelAlert}>
          <AddExternalModel
            path="forms/model_hub/add_to_model_hub"
            useOwnershipAccessToggles
            close={closeAddModelDrawer}
            onModelAdded={() => window.location.reload()}
            alert={addModelAlert}
            clearAlert={() => setAddModelAlert()}
          />
        </alertContext.Provider>
      )}
    </>
  );

  if (loading && !info) {
    return detailShell(
      <div className="training-detail-panel">
        <div className="training-detail-fetching-state">
          <Loader />
          <h5 className="fw-semibold mb-2">Loading model version details...</h5>
          <p className="text-secondary mb-0">Fetching model information and benchmark metrics.</p>
        </div>
      </div>
    );
  }

  if (error || !info) {
    return detailShell(
      <div className="training-detail-panel deploy-model-detail__error">
        <ErrorScreen message="Unable to load model version detail" />
        <Button color="primary" onClick={refetch}>Retry</Button>
      </div>
    );
  }

  const modelArchitecture = firstValue(
    info.model_architecture,
    selectedCard?.model_architecture,
    listedModel?.model_architecture,
    architecture,
    info.name,
    "-"
  );
  const rawTaskType = firstValue(
    info.task_type,
    selectedCard?.task_type,
    listedModel?.task_type,
    modelConfig?.exp_config?.task_type,
    modelConfig?.exp_config?.task,
    modelConfig?.Model_Info?.["Task Type"],
    info.model_class
  );
  const taskType = titleCase(rawTaskType, "Model");
  const dataset = titleCase(firstValue(
    info.benchmark_dataset,
    selectedCard?.benchmark_dataset,
    selectedCard?.dataset,
    listedModel?.benchmark_dataset,
    listedModel?.dataset,
    modelConfig?.exp_config?.data?.ds_name
  ), "-");
  const framework = firstValue(info.framework, selectedCard?.framework, listedModel?.framework);
  const modelVariant = firstValue(info.model_variant, selectedCard?.model_variant, listedModel?.model_variant);
  const inputResolution = firstValue(
    info.input_resolution,
    selectedCard?.input_resolution,
    listedModel?.input_resolution,
    modelConfig?.Model_Info?.Preprocessing?.["max input resolution"],
    "-"
  );
  const metricName = firstValue(benchmarkInfo?.["Metric Name"], info?.metric_name, "Accuracy");
  const metricValues = benchmarkRows.map((row) => toNumber(row.accuracy)).filter((value) => value !== null);
  const bestMetric = metricValues.length ? Math.max(...metricValues) : null;

  const openEvaluation = () => {
    const params = new URLSearchParams({
      model_name: info.name || decodedModelName,
      version: String(version || ""),
    });
    navigate(`/projects/${projectId}/deepdeploy/${encodeURIComponent(architecture)}/evaluation?${params.toString()}`, {
      state: { selectedCard: { ...listedModel, ...selectedCard, ...info }, through_project: true, version, projectId, architecture },
    });
  };

  const showDeployUnavailable = (targetId) => {
    if (deployPopoverTimeoutRef.current) {
      clearTimeout(deployPopoverTimeoutRef.current);
    }
    setDeployPopoverTarget(targetId);
    setShowDeployPopover(true);
    deployPopoverTimeoutRef.current = setTimeout(() => {
      setShowDeployPopover(false);
      deployPopoverTimeoutRef.current = null;
    }, 2000);
  };

  const cards = [
    {
      id: "task",
      label: "Task Type",
      shortLabel: "Task",
      shortValue: shortValue(taskType, 9),
      title: taskType,
      description: `This model version is configured for ${taskType.toLowerCase()} using ${dataset}.`,
      details: [
        { label: "Dataset", value: dataset },
        { label: "Version", value: formatVersion(version) || "-" },
        // { label: "Source", value: titleCase(firstValue(info.import_source, selectedCard?.import_source, listedModel?.import_source), "DeepEdge") },
      ],
      tone: "green",
      icon: <FontAwesomeIcon icon={faListCheck} className="wf-icon-small" />,
    },
    {
      id: "architecture",
      label: "Architecture",
      shortLabel: "Model",
      shortValue: shortValue(modelArchitecture, 9),
      title: titleCase(modelArchitecture),
      description: `${titleCase(framework, "Configured framework")} model prepared for evaluation and deployment.`,
      details: [
        { label: "Architecture", value: titleCase(modelArchitecture) },
        { label: "Framework", value: titleCase(framework) },
        { label: "Variant", value: titleCase(modelVariant) },
      ],
      tone: "red",
      icon: <ModelIcon className="wf-icon-small" />,
    },
    {
      id: "classes",
      label: "Classes",
      shortLabel: "Classes",
      shortValue: classNames.length || "-",
      title: classNames.length ? `${classNames.length} target classes` : "No class labels",
      description: classNames.length ? "Classes available to this model version." : "No class labels were supplied for this model version.",
      details: [
        { label: "Class count", value: classNames.length || "-" },
        { label: "Labels", value: <ClassLabelsPreview classNames={classNames} expandMode="popup" maxVisible={3} /> },
      ],
      tone: "amber",
      icon: <LabelsIcon className="wf-icon-small" />,
    },
    {
      id: "performance",
      label: "Performance",
      shortLabel: titleCase(metricName),
      shortValue: bestMetric !== null ? `${bestMetric}%` : "-",
      title: bestMetric !== null ? `${bestMetric}%` : "Awaiting results",
      description: bestMetric !== null ? `Best ${titleCase(metricName)} result reported for this version.` : "Benchmark results have not been reported yet.",
      details: [
        { label: "Metric", value: titleCase(metricName) },
        { label: "Targets", value: benchmarkRows.length || "-" },
        { label: "Input", value: inputResolution === "-" ? "-" : `${inputResolution}px` },
      ],
      tone: "blue",
      icon: <AccuracyIcon2 className="wf-icon-small" />,
    },
  ];

  return detailShell(
    <div className="training-detail-panel deploy-model-detail">
      <div className="training-panel container-card dataset-detail-header dataset-detail-header--with-insight training-detail-header-card">
        <div className="dataset-detail-header-content d-flex justify-content-between align-items-center w-100">
          <div className="dataset-detail-title-block">
            <div className="dataset-detail-identity">
              <span className="dataset-detail-thumb deploy-model-detail__header-icon" aria-hidden="true">
                <ModelIcon />
              </span>
              <div className="dataset-detail-identity-copy">
                <h6 className="second-title mb-1 training-detail-identity-title">
                  {displayName}
                  {formatVersion(version) && <span className="second-title text-primary">- {formatVersion(version)}</span>}
                  {/* <span className="deploy-model-detail__ready">Ready</span> */}
                </h6>
                <div className="d-a gap-4 text-secondary flex-wrap">
                  <span className="d-j-a gap-1" style={{ width: "fit-content" }}>
                    <img src="/ico/user.png" width={15} height={15} alt="" />
                    {titleCase(firstValue(info.organization, info.ownership), "DeepEdge")}
                  </span>
                  {firstValue(info.updated_on, info.created_on) && (
                    <span className="d-j-a gap-1" style={{ width: "fit-content" }}>
                      <CalendarIcon className="wf-icon" style={{ width: 15.5, height: 15.5 }} />
                      {DateParse(firstValue(info.updated_on, info.created_on))}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="dataset-detail-header-actions d-flex align-items-start gap-2 flex-wrap">
            <ModelVersionActionInsight
              version={version}
              activeIndex={actionInsightIndex}
              onActiveIndexChange={setActionInsightIndex}
              onEvaluate={openEvaluation}
              onDeploy={() => showDeployUnavailable("deploy-version-model-button")}
            />

            <div className="dataset-detail-header-menu ms-2">
              <div className="card-action-menu-wrap" onClick={(event) => event.stopPropagation()}>
                <UncontrolledDropdown>
                  <DropdownToggle
                    id="deploy-version-actions-menu-button"
                    icon="true"
                    className="card-action-menu__trigger"
                    aria-label="Model version actions"
                  >
                    <FontAwesomeIcon icon={faEllipsisV} />
                  </DropdownToggle>
                  <DropdownMenu end container="body" className="card-action-menu p-1 overflow-hidden">
                    <DropdownItem
                      className="card-action-menu__item"
                      onClick={() => {
                        setActionInsightIndex(0);
                        openEvaluation();
                      }}
                    >
                      <FontAwesomeIcon icon={faBolt} className="card-action-menu__icon card-action-menu__icon--view" />
                      Evaluate Model
                    </DropdownItem>
                    <DropdownItem
                      className="card-action-menu__item"
                      onClick={() => {
                        setActionInsightIndex(1);
                        showDeployUnavailable("deploy-version-actions-menu-button");
                      }}
                    >
                      <FontAwesomeIcon icon={faRocket} className="card-action-menu__icon card-action-menu__icon--power" />
                      Deploy
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>

              <div
                className={`dataset-version-insight__dots dataset-version-insight__dots--menu dataset-version-insight__dots--${actionInsightIndex === 0 ? "task" : "experiment"}`}
                role="tablist"
                aria-label="Model version actions"
              >
                {["Evaluate Model", "Deploy"].map((label, index) => (
                  <button
                    key={label}
                    type="button"
                    role="tab"
                    aria-selected={index === actionInsightIndex}
                    aria-label={label}
                    title={label}
                    className={`dataset-version-insight__dot${index === actionInsightIndex ? " is-active" : ""}`}
                    onClick={() => setActionInsightIndex(index)}
                  />
                ))}
              </div>
            </div>

            <Popover
              placement="bottom"
              isOpen={showDeployPopover}
              target={deployPopoverTarget}
              toggle={() => setShowDeployPopover(false)}
            >
              <PopoverBody>Coming soon — this feature is not available yet.</PopoverBody>
            </Popover>
          </div>
        </div>
      </div>

      <ExpandableModelCards cards={cards} />

      <div className="training-detail-tab-panel">
        <div className="training-detail-tab-bar" role="tablist" aria-label="Model version detail sections">
          <button
            type="button"
            role="tab"
            aria-selected={activePanel === "metrics"}
            className={`d-a gap-1 plain-button2 text-nowrap px-3 py-1 fw-normal ${activePanel === "metrics" ? "bg-main rounded text-white" : "text"}`}
            onClick={() => setActivePanel("metrics")}
          >
            <GraphIcon className="wf-icon-small" /> Metrics
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activePanel === "configuration"}
            className={`d-a gap-1 plain-button2 text-nowrap px-3 py-1 fw-normal ${activePanel === "configuration" ? "bg-main rounded text-white" : "text"}`}
            onClick={() => setActivePanel("configuration")}
          >
            <ModifyOperationsIcon className="wf-icon-small" /> Configuration
          </button>
        </div>

        <div className="training-detail-panel-section">
          {activePanel === "metrics" ? (
            <ModelMetrics rows={benchmarkRows} metricName={metricName} />
          ) : (
            <ModelConfiguration
              info={{
                ...listedModel,
                ...selectedCard,
                ...info,
                framework,
                model_variant: modelVariant,
                benchmark_dataset: dataset,
                input_resolution: inputResolution,
                config: modelConfig,
              }}
              classNames={classNames}
            />
          )}
        </div>
      </div>
    </div>
  );
}
